import { useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Download, FolderInput, Pencil, Trash2, UploadCloud } from 'lucide-react';
import { formatByteSize, formatDate } from '@xdocs/i18n';
import type { ExplorerEntry } from '@xdocs/schemas';
import { Badge, Button, DataTable, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { UploadModal } from '../../features/uploads/index.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { ExplorerEntryIcon } from './fileIcons.js';
import { FilePreviewPanel } from './FilePreviewPanel.js';
import type { MoveTarget } from './MoveDialog.js';
import { MoveDialog } from './MoveDialog.js';
import type { RenameTarget } from './RenameDialog.js';
import { RenameDialog } from './RenameDialog.js';
import { NewFolderDialog } from './NewFolderDialog.js';
import { useExplorerList } from './useExplorerList.js';
import { useExplorerMutations } from './useExplorerMutations.js';
import { useExplorerT } from './useExplorerT.js';
import styles from './ExplorerPage.module.css';

const STATUS_TONE: Record<string, BadgeTone> = {
  pending: 'neutral',
  uploaded: 'info',
  ingesting: 'info',
  ready: 'success',
  failed: 'error',
};

/**
 * `/explorer` + `/explorer/preview/:fileId` (F7). Server-side paginated/
 * sorted/searched folder browser over `<DataTable>` (E4 §8.3 — no bespoke
 * grid, despite the mockup showing a card grid; the hard rule wins per this
 * task's LAW). The "name" cell (not the row) is the click target for
 * open/navigate specifically so per-row action buttons never nest inside
 * `<DataTable>`'s own row-level `<button>` on the mobile card layout
 * (invalid/inaccessible nested interactive controls).
 */
export function ExplorerPage(): ReactElement {
  const navigate = useNavigate();
  const { fileId: previewFileId } = useParams<{ fileId?: string }>();
  const { locale } = useI18n();
  const { t } = useExplorerT();
  const [folderId, setFolderId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<RenameTarget | null>(null);
  const [moveTarget, setMoveTarget] = useState<MoveTarget | null>(null);

  const { rows, total, nextCursor, breadcrumbs, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry } =
    useExplorerList(folderId);
  const { pendingDeleteIds, requestDeleteFile, requestDeleteFolder, renameFile, renameFolder, moveFile, moveFolder, createFolder } =
    useExplorerMutations();

  const visibleRows = rows.filter((row) => !pendingDeleteIds.has(row.id));

  function openEntry(entry: ExplorerEntry): void {
    if (entry.type === 'folder') {
      setFolderId(entry.id);
      return;
    }
    void navigate(`/explorer/preview/${entry.id}`);
  }

  const columns: ColumnDef<ExplorerEntry>[] = [
    {
      id: 'name',
      header: t('explorer:column.name'),
      sortable: true,
      primary: true,
      cell: (entry) => (
        <button type="button" className={cn(styles['nameCell'])} onClick={() => openEntry(entry)}>
          <span className={cn(styles['nameIcon'])} aria-hidden="true">
            <ExplorerEntryIcon entry={entry} />
          </span>
          <span className={cn(styles['nameText'])}>{entry.name}</span>
        </button>
      ),
    },
    {
      id: 'kind',
      header: t('explorer:column.kind'),
      cell: (entry) => (entry.type === 'folder' ? t('explorer:kind.folder') : t(`explorer:kind.${entry.kind}` as Parameters<typeof t>[0])),
      hideOnMobile: true,
    },
    {
      id: 'byteSize',
      header: t('explorer:column.size'),
      sortable: true,
      cell: (entry) => (entry.type === 'file' ? formatByteSize(locale, entry.byteSize) : '—'),
      hideOnMobile: true,
    },
    {
      // mockup-22: file cards show a small pill badge for where the file came
      // from (`f.badge` — "uploaded" / "generated" / drive-sourced), derived
      // here from the `provenance` field the schema already carries but this
      // table never surfaced. Folders have no provenance, so the cell is blank.
      id: 'origin',
      header: t('explorer:column.origin'),
      cell: (entry) =>
        entry.type === 'file' ? <Badge tone="info">{t(`explorer:provenance.${entry.provenance}` as Parameters<typeof t>[0])}</Badge> : null,
      hideOnMobile: true,
    },
    {
      id: 'updatedAt',
      header: t('explorer:column.updatedAt'),
      sortable: true,
      cell: (entry) => <span className={cn(styles['metaCell'])}>{formatDate(locale, entry.updatedAt, { dateStyle: 'medium' })}</span>,
    },
    {
      id: 'status',
      header: t('explorer:column.status'),
      cell: (entry) =>
        entry.type === 'file' ? <Badge tone={STATUS_TONE[entry.status] ?? 'neutral'}>{t(`explorer:status.${entry.status}` as Parameters<typeof t>[0])}</Badge> : null,
      hideOnMobile: true,
    },
    {
      id: 'actions',
      header: t('explorer:column.actions'),
      cell: (entry) => (
        <div className={cn(styles['actions'])}>
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('explorer:action.rename')}
            onClick={(event) => {
              event.stopPropagation();
              setRenameTarget({ type: entry.type, id: entry.id, name: entry.name });
            }}
          >
            <Pencil size={14} aria-hidden="true" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('explorer:action.move')}
            onClick={(event) => {
              event.stopPropagation();
              setMoveTarget({ type: entry.type, id: entry.id, name: entry.name, currentFolderId: entry.folderId });
            }}
          >
            <FolderInput size={14} aria-hidden="true" />
          </Button>
          {entry.type === 'file' && (
            <Button size="icon" variant="ghost" aria-label={t('explorer:action.download')} disabled disabledReason={t('explorer:preview.downloadUnavailable')}>
              <Download size={14} aria-hidden="true" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            aria-label={t('explorer:action.delete')}
            onClick={(event) => {
              event.stopPropagation();
              if (entry.type === 'folder') requestDeleteFolder(entry);
              else requestDeleteFile(entry);
            }}
          >
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <div>
          <div className={cn(styles['eyebrow'])}>{t('explorer:eyebrow')}</div>
          <h1 className={cn(styles['title'])}>{t('explorer:title')}</h1>
        </div>
        <div className={cn(styles['toolbar'])}>
          <Button variant="secondary" leadingIcon={<UploadCloud size={15} aria-hidden="true" />} onClick={() => setUploadOpen(true)}>
            {t('explorer:action.upload')}
          </Button>
          <Button variant="secondary" onClick={() => void navigate('/settings/drive')}>
            {t('explorer:action.connectDrive')}
          </Button>
          <Button variant="primary" onClick={() => setNewFolderOpen(true)}>
            {t('explorer:action.newFolder')}
          </Button>
        </div>
      </div>

      <nav className={cn(styles['breadcrumbs'])} aria-label={t('explorer:title')}>
        <button type="button" className={cn(styles['breadcrumbButton'])} onClick={() => setFolderId(null)}>
          {t('explorer:breadcrumb.home')}
        </button>
        {breadcrumbs.map((crumb) => (
          <span key={crumb.id} className={cn(styles['breadcrumbSegment'])}>
            <span aria-hidden="true">/</span>
            <button type="button" className={cn(styles['breadcrumbButton'])} onClick={() => setFolderId(crumb.id)}>
              {crumb.name}
            </button>
          </span>
        ))}
        {breadcrumbs.length === 0 && (
          <span className={cn(styles['breadcrumbSegment'])}>
            <span aria-hidden="true">/</span>
            <span className={cn(styles['breadcrumbCurrent'])}>{t('explorer:breadcrumb.allFiles')}</span>
          </span>
        )}
      </nav>

      <DataTable
        columns={columns}
        rows={visibleRows}
        rowKey={(entry) => entry.id}
        query={query}
        onQueryChange={onQueryChange}
        label={t('explorer:title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        emptyTitle={t('explorer:empty.title')}
        emptyDescription={t('explorer:empty.description')}
        emptyAction={{ label: t('explorer:empty.cta'), onClick: () => setUploadOpen(true) }}
        noResultsTitle={t('explorer:noResults.title', { query: query.q ?? '' })}
        onClearFilters={() => {
          const { q: _q, ...rest } = query;
          onQueryChange({ ...rest, page: 1 });
        }}
        searchLabel={t('explorer:searchLabel')}
        searchPlaceholder={t('explorer:searchPlaceholder')}
      />

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} folderId={folderId} />
      <NewFolderDialog open={newFolderOpen} onClose={() => setNewFolderOpen(false)} parentFolderId={folderId} onCreate={createFolder} />
      <RenameDialog target={renameTarget} onClose={() => setRenameTarget(null)} onRenameFile={renameFile} onRenameFolder={renameFolder} />
      <MoveDialog target={moveTarget} onClose={() => setMoveTarget(null)} onMoveFile={moveFile} onMoveFolder={moveFolder} />

      {previewFileId && <FilePreviewPanel fileId={previewFileId} onClose={() => void navigate('/explorer')} />}
    </div>
  );
}
